import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { BandGallery } from "./BandGallery";

const pillars = [
  { k: "Ambassador", t: "Reduce Inequalities Together", d: "Lending her platform to the people and causes working to close the gaps of income, opportunity and dignity." },
  { k: "Together Band", t: "Wear the Goal", d: "Each Together Band stands for one of the 17 Global Goals. Mimi wears Goal 10 — a daily, visible promise." },
  { k: "Global Goals", t: "Leave no one behind", d: "The pledge at the heart of the UN 2030 Agenda for Sustainable Development, and the heart of her advocacy." },
];

const links = [
  ["Global Goal 10", "https://www.globalgoals.org/goals/10-reduced-inequalities/"],
  ["Together Band", "https://togetherband.org/"],
  ["UN SDG 10", "https://sdgs.un.org/goals/goal10"],
];

export const Activism = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["8%", "-12%"]);
  return (
    <section id="activism" ref={ref} data-testid="activism-section" className="relative bg-[var(--nude)] text-[var(--ink)] pt-36 sm:pt-44 pb-28 overflow-hidden">
      <motion.div style={{ x }} aria-hidden
        className="pointer-events-none absolute -top-10 right-0 font-display italic leading-none text-[48vw] sm:text-[38vw] text-[#c9a46a]/20 select-none">
        10
      </motion.div>
      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.32em] text-[var(--goal10)] mb-6">03 — Activism · United Nations Global Goal 10</p>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            Reduce <em>inequalities</em>,<br /> together.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-10 mt-16 sm:mt-24">
          <Reveal className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="/img/goal10-mimi.jpg" alt="Mimi Tao wearing her Goal 10 Together Band" className="h-full w-full object-cover object-top" />
              <div className="absolute top-0 left-0 bg-[var(--goal10)] text-white px-5 py-4">
                <p className="text-[11px] uppercase tracking-[0.2em]">Goal</p>
                <p className="font-display text-5xl leading-none">10</p>
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7 flex flex-col justify-between gap-10">
            <Reveal>
              <p className="font-display text-2xl sm:text-3xl leading-snug">
                “Reduce inequality within and among countries.” Ten words from the United Nations — and the reason Mimi uses every stage she is given.
              </p>
              <p className="mt-6 text-base sm:text-lg text-zinc-700 font-light leading-relaxed">
                As an ambassador for <strong className="font-medium">Reduce Inequalities Together</strong>, she champions the Global Goals for Sustainable Development — speaking up for inclusion, equal opportunity and the dignity of every person, regardless of where they were born, who they love or what they own.
              </p>
            </Reveal>
            <div className="divide-y divide-black/10 border-y border-black/10">
              {pillars.map((p, i) => (
                <Reveal key={p.k} delay={i * 0.1}>
                  <div data-testid={`activism-pillar-${i}`} className="grid grid-cols-12 gap-4 py-6">
                    <span className="col-span-12 sm:col-span-4 text-[11px] uppercase tracking-[0.25em] text-[var(--goal10)] pt-1">{p.k}</span>
                    <div className="col-span-12 sm:col-span-8">
                      <h3 className="font-display text-2xl">{p.t}</h3>
                      <p className="mt-2 text-zinc-700 text-sm sm:text-base leading-relaxed">{p.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              {links.map(([l, h]) => (
                <a key={l} href={h} target="_blank" rel="noreferrer" data-testid={`activism-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[var(--goal10)] hover:border-[var(--goal10)] hover:text-white transition-colors duration-500">
                  {l} <ArrowUpRight size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <BandGallery />
        <Reveal className="mt-20 sm:mt-28">
          <figure data-testid="activism-sdgs-image" className="max-w-5xl mx-auto bg-white">
            <img src="/img/sdgs.jpg" alt="The 17 UN Sustainable Development Goals" loading="lazy" className="w-full h-auto" />
          </figure>
          <p data-testid="activism-sdgs-caption" className="max-w-5xl mx-auto mt-6 text-center text-[12px] sm:text-sm uppercase tracking-[0.25em] text-stone-700">
            Proud ambassador for <span className="text-[var(--goal10)]">Goal 10 — Reduced Inequalities</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
